import React, { useRef, useEffect, useState } from 'react';
import { Button, Select, Space, message, Spin, Divider, Tooltip } from 'antd';
import { 
  PlayCircleOutlined,
  SaveOutlined,
  UndoOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  CopyOutlined,
  DownloadOutlined,
  SettingOutlined
} from '@ant-design/icons';
import MonacoEditor from 'react-monaco-editor';

const { Option } = Select;

const CodeEditor = ({ 
  initialCode = '// 请在此处编写代码\n',
  language = 'javascript',
  theme = 'vs-dark',
  height = '500px',
  onRun,
  onSave,
  readOnly = false
}) => {
  const editorRef = useRef(null);
  const [code, setCode] = useState(initialCode);
  const [currentLanguage, setCurrentLanguage] = useState(language);
  const [currentTheme, setCurrentTheme] = useState(theme);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const containerRef = useRef(null);

  // 支持的编程语言
  const languages = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'cpp', label: 'C++' },
    { value: 'csharp', label: 'C#' },
  ];

  // 支持的编辑器主题
  const themes = [
    { value: 'vs', label: '浅色主题' },
    { value: 'vs-dark', label: '深色主题' },
    { value: 'hc-black', label: '高对比度' },
  ];

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isFullscreen]);

  // 编辑器配置选项
  const editorOptions = {
    selectOnLineNumbers: true,
    roundedSelection: false,
    readOnly: readOnly,
    cursorStyle: 'line',
    automaticLayout: true,
    minimap: { enabled: true },
    scrollBeyondLastLine: false,
    lineNumbers: 'on',
    folding: true,
    fontSize: 14,
    fontFamily: 'Consolas, "Courier New", monospace',
  };

  const handleEditorDidMount = (editor) => {
    editorRef.current = editor;
    editor.focus();
  };

  const handleLanguageChange = (value) => {
    setCurrentLanguage(value);
  };

  const handleThemeChange = (value) => {
    setCurrentTheme(value);
  };

  const handleCodeChange = (newValue) => {
    setCode(newValue);
  };

  const handleRun = () => {
    if (isRunning) return;
    
    setIsRunning(true);
    try {
      onRun && onRun(code, currentLanguage);
    } catch (error) {
      message.error('代码执行失败');
      console.error(error);
    } finally {
      setTimeout(() => {
        setIsRunning(false);
      }, 1000);
    }
  };

  const handleSave = () => {
    onSave && onSave(code, currentLanguage);
    message.success('代码已保存');
  };

  const handleRestore = () => {
    setCode(initialCode);
    message.info('代码已重置');
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code)
      .then(() => message.success('代码已复制到剪贴板'))
      .catch(() => message.error('复制失败，请手动复制'));
  };

  const handleDownload = () => {
    const fileExtensions = {
      javascript: 'js',
      python: 'py',
      java: 'java',
      cpp: 'cpp',
      csharp: 'cs'
    };
    
    const extension = fileExtensions[currentLanguage] || 'txt';
    const filename = `code.${extension}`;
    const blob = new Blob([code], { type: 'text/plain' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    
    URL.revokeObjectURL(link.href);
    message.success(`代码已下载为 ${filename}`);
  };

  return (
    <div 
      ref={containerRef}
      style={{ 
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        right: isFullscreen ? 0 : 'auto',
        bottom: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 1000 : 'auto',
        background: '#1e1e1e',
        padding: '10px',
        borderRadius: isFullscreen ? 0 : '4px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '10px',
        backgroundColor: '#252526',
        padding: '8px 16px',
        borderRadius: '4px'
      }}>
        <Space>
          <Select
            value={currentLanguage}
            onChange={handleLanguageChange}
            style={{ width: 120 }}
            disabled={readOnly}
          >
            {languages.map(lang => (
              <Option key={lang.value} value={lang.value}>{lang.label}</Option>
            ))}
          </Select>
          
          <Select
            value={currentTheme}
            onChange={handleThemeChange}
            style={{ width: 120 }}
          >
            {themes.map(t => (
              <Option key={t.value} value={t.value}>{t.label}</Option>
            ))}
          </Select>
        </Space>
        
        <Space>
          {!readOnly && (
            <>
              <Tooltip title="重置代码">
                <Button 
                  icon={<UndoOutlined />} 
                  onClick={handleRestore}
                />
              </Tooltip>
              
              <Tooltip title="保存代码">
                <Button 
                  icon={<SaveOutlined />} 
                  onClick={handleSave}
                />
              </Tooltip>
              
              <Tooltip title="运行代码">
                <Button 
                  type="primary" 
                  icon={<PlayCircleOutlined />} 
                  onClick={handleRun}
                  loading={isRunning}
                >
                  运行
                </Button>
              </Tooltip>
              
              <Divider type="vertical" />
            </>
          )}
          
          <Tooltip title="复制代码">
            <Button 
              icon={<CopyOutlined />} 
              onClick={handleCopyCode}
            />
          </Tooltip>
          
          <Tooltip title="下载代码">
            <Button 
              icon={<DownloadOutlined />} 
              onClick={handleDownload}
            />
          </Tooltip>
          
          <Tooltip title={isFullscreen ? "退出全屏" : "全屏编辑"}>
            <Button 
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} 
              onClick={handleToggleFullscreen}
            />
          </Tooltip>
        </Space>
      </div>
      
      <div style={{ flex: 1, position: 'relative' }}>
        <MonacoEditor
          language={currentLanguage}
          theme={currentTheme}
          value={code}
          options={editorOptions}
          onChange={handleCodeChange}
          editorDidMount={handleEditorDidMount}
          height={isFullscreen ? 'calc(100vh - 70px)' : height}
          width="100%"
        />
        
        {isRunning && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            borderRadius: '4px'
          }}>
            <Spin size="large" tip="代码运行中..." />
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeEditor;  
